import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAutoAssignCoordinators,
  saveCoordinatorSettings,
} from "../../../business/redux/reducers/mcOpsDashboard/actions";
import { Icon } from "../icons";
import { BoardTitleRow } from "../components/JobSearch";

/**
 * Admin page: what each Move Coordinator can take in auto-assignment, and the
 * load the engine sees for them right now.
 */

const MOVE_TYPE_LABELS = { local: "Local", ld: "Long distance", storage: "Storage" };
const SIZE_LABELS = { small: "Small (≤ 800 cu ft)", medium: "Medium (≤ 1,500 cu ft)", large: "Large (any)" };

const Row = ({ coordinator, options, onSave }) => {
  const [draft, setDraft] = useState(coordinator.settings);
  const [status, setStatus] = useState("idle"); // idle | saving | saved | failed
  const id = (field) => `mc-${coordinator.id}-${field}`;
  const dirty = JSON.stringify(draft) !== JSON.stringify(coordinator.settings);

  useEffect(() => {
    setDraft(coordinator.settings);
  }, [coordinator.settings]);

  const set = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setStatus("idle");
  };
  const toggleIn = (field, value) =>
    set({
      [field]: draft[field].includes(value)
        ? draft[field].filter((v) => v !== value)
        : [...draft[field], value],
    });

  const save = async () => {
    setStatus("saving");
    try {
      await onSave(coordinator.id, draft);
      setStatus("saved");
    } catch (e) {
      setStatus("failed");
    }
  };

  const load = coordinator.load || {};

  return (
    <tr className={draft.active ? "" : "mc-row-off"}>
      <td>
        <div className="mc-name">{coordinator.name}</div>
        <div className="meta">
          {coordinator.configured ? "Custom settings" : "Defaults"}
        </div>
      </td>
      <td>
        <label className="mc-check">
          <input
            type="checkbox"
            aria-label="Active"
            checked={!!draft.active}
            onChange={(e) => set({ active: e.target.checked })}
          />
          Active
        </label>
      </td>
      <td>
        {(options.moveTypes || []).map((t) => (
          <label className="mc-check" key={t}>
            <input
              type="checkbox"
              aria-label={MOVE_TYPE_LABELS[t] || t}
              checked={draft.move_types.includes(t)}
              onChange={() => toggleIn("move_types", t)}
            />
            {MOVE_TYPE_LABELS[t] || t}
          </label>
        ))}
      </td>
      <td>
        <div className="mc-chips">
          {(options.branches || []).map((b) => (
            <button
              type="button"
              key={b}
              className={`chip${draft.branches.includes(b) ? " active" : ""}`}
              aria-pressed={draft.branches.includes(b)}
              onClick={() => toggleIn("branches", b)}
            >
              {b}
            </button>
          ))}
        </div>
        <div className="meta">{draft.branches.length ? "" : "Any branch"}</div>
      </td>
      <td>
        <select
          aria-label="Largest job"
          value={draft.max_job_size}
          onChange={(e) => set({ max_job_size: e.target.value })}
        >
          {(options.sizes || []).map((s) => (
            <option value={s} key={s}>
              {SIZE_LABELS[s] || s}
            </option>
          ))}
        </select>
        <label className="mc-check" style={{ marginTop: 6 }}>
          <input
            type="checkbox"
            aria-label="VIP"
            checked={!!draft.handles_vip}
            onChange={(e) => set({ handles_vip: e.target.checked })}
          />
          VIP clients
        </label>
      </td>
      <td>
        <label className="mc-cap">
          <span>Per day</span>
          <input
            type="number"
            min="1"
            max="100"
            aria-label="Per day"
            value={draft.daily_cap}
            onChange={(e) => set({ daily_cap: Number(e.target.value) })}
          />
        </label>
        <label className="mc-cap">
          <span>Per run</span>
          <input
            type="number"
            min="1"
            max="50"
            aria-label="Per run"
            value={draft.run_cap}
            onChange={(e) => set({ run_cap: Number(e.target.value) })}
          />
        </label>
      </td>
      <td className="mc-load">
        <div>{load.givenToday ?? 0} given today</div>
        <div>{load.new3h ?? 0} new in 3h</div>
        <div>{load.overdue72h ?? 0} overdue</div>
        <div>{load.callsDue ?? 0} calls due</div>
        <div className="meta">{load.openJobs ?? 0} open jobs</div>
      </td>
      <td>
        <button
          type="button"
          className={`btn small${status === "saved" ? " success" : " primary"}`}
          disabled={status === "saving" || (!dirty && status !== "saved")}
          onClick={save}
        >
          <Icon name="check" />
          {status === "saving" ? "Saving…" : status === "saved" ? "Saved" : "Save"}
        </button>
        {status === "failed" ? <div className="meta mc-error">Could not save</div> : null}
      </td>
    </tr>
  );
};

export const CoordinatorsPage = () => {
  const dispatch = useDispatch();
  const data = useSelector((s) => s.mcOpsDashboard?.meta?.autoAssign);

  useEffect(() => {
    dispatch(fetchAutoAssignCoordinators());
  }, [dispatch]);

  const onSave = (userId, settings) => dispatch(saveCoordinatorSettings(userId, settings));

  return (
    <div className="page active">
      <BoardTitleRow title="Coordinators" />

      {data?.error === 403 ? (
        <div className="panel-empty">Admin only. Sign in as a manager to edit coordinator settings.</div>
      ) : !data ? (
        <div className="panel-empty">Loading…</div>
      ) : (
        <>
          <p className="mc-intro">
            Auto-assignment is in <strong>{data.mode} mode</strong>
            {data.mode === "suggest"
              ? ": every 3 hours it logs which coordinator each new job would go to. Nothing is written to Zoho until the mode is switched."
              : ": every 3 hours it assigns new jobs and writes the coordinator to Zoho."}
          </p>
          <div className="mc-table-wrap">
            <table className="mc-table">
              <thead>
                <tr>
                  <th>Coordinator</th>
                  <th>Status</th>
                  <th>Move types</th>
                  <th>Branches</th>
                  <th>Jobs allowed</th>
                  <th>Caps</th>
                  <th>Load now</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.coordinators.map((c) => (
                  <Row coordinator={c} options={data.options} onSave={onSave} key={c.id} />
                ))}
              </tbody>
            </table>
          </div>
          {!data.coordinators.length ? (
            <div className="panel-empty">No coordinators found.</div>
          ) : null}
        </>
      )}
    </div>
  );
};
